import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { ThemeProvider } from '@mui/system';
import { Tooltip, IconButton } from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import * as DefaultRoutes from '@Routes/DefaultRoutes';
import { MY_EXEMPTION_REQUESTS, EXIT_PLAN_BY_ID } from '@API/APIRoutes';
import GenericRnDListing from '@Pages/RnDMember/GenericRnDListing/GenericRnDListing';
import { DeletePopup } from '@Components/Popup/DeletePopup';
import { setSnackbar } from '@Redux/snackBarSlice';
import axiosInstance from '@API/axios';
import { EDITABLE_PLAN_STATUSES } from '@Utils/planType';
import { buildExemptionColumns, DEFAULT_EXEMPTION_COLUMN_VISIBILITY } from './exemptionColumns';

const PAGE_TITLE = 'My Regulatory Exemptions';
const ERROR_MESSAGE = 'Failed to fetch exemption requests. Trying again!';
const DELETE_SUCCESS_MESSAGE = 'Exemption request deleted successfully!';
const DELETE_ERROR_MESSAGE = 'Failed to delete exemption request.';
const DELETE_POPUP_ENTITY = 'exemption request';
const TOOLTIP_VIEW_DETAILS = 'Open Exemption Request';
const TOOLTIP_DELETE = 'Delete Exemption Request';

/**
 * The R&D member's / RD Manager's Exemptions Hub, mirroring the Exit Plans directory: every
 * exemption request they created, in any status, server-paginated and filterable. Deleting is
 * offered only while the request is still theirs to change; once it is with the regulatory owner
 * or the approvers the backend refuses it too.
 */
function MyExemptionsListing() {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const columns = buildExemptionColumns({ actionsWidth: 120 });

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeletePopupOpen(true);
  };

  const handleClosePopup = () => {
    setDeletePopupOpen(false);
    setIdToDelete(null);
  };

  const handleConfirmDelete = async (id) => {
    setDeleteLoading(true);
    try {
      await axiosInstance.delete(EXIT_PLAN_BY_ID(id));
      setRows((prevRows) => prevRows.filter((row) => row.id !== id));
      dispatch(setSnackbar({ open: true, message: DELETE_SUCCESS_MESSAGE, error: false }));
      handleClosePopup();
    } catch (error) {
      dispatch(setSnackbar({
        open: true,
        message: error?.response?.data?.message || DELETE_ERROR_MESSAGE,
        error: true,
      }));
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderActions = (params, navigate, theme) => (
    <ThemeProvider theme={theme}>
      <Tooltip title={TOOLTIP_VIEW_DETAILS}>
        <IconButton
          color="info"
          size="small"
          onClick={() => navigate(DefaultRoutes.exemptionInfo.replace(':planId', params.row.id))}
        >
          <LightbulbOutlinedIcon />
        </IconButton>
      </Tooltip>
      {EDITABLE_PLAN_STATUSES.includes(params.row.status) && (
        <Tooltip title={TOOLTIP_DELETE}>
          <IconButton color="error" size="small" onClick={() => handleDeleteClick(params.row.id)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )}
    </ThemeProvider>
  );

  return (
    <>
      <GenericRnDListing
        pageTitle={PAGE_TITLE}
        PageIcon={<ReceiptLongIcon color="white" fontSize="large" />}
        iconColor="warning.main"
        apiRoute={MY_EXEMPTION_REQUESTS}
        columns={columns}
        renderActions={renderActions}
        errorMessage={ERROR_MESSAGE}
        initialColumnVisibilityModel={DEFAULT_EXEMPTION_COLUMN_VISIBILITY}
        serverPagination={true}
        rows={rows}
        setRows={setRows}
        getRowId={(row) => row.id}
      />
      {DeletePopup(
        DELETE_POPUP_ENTITY,
        deletePopupOpen,
        handleClosePopup,
        handleConfirmDelete,
        idToDelete,
        deleteLoading,
        'delete'
      )}
    </>
  );
}

export default MyExemptionsListing;
